import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../api/errors.js';
import { champsEnErreur, erreursParChamp } from '../api/erreursChamps.js';

export interface ErreurFormulaire {
  /** Message d'ensemble, sous le formulaire ; `null` sans erreur. */
  readonly erreur: string | null;
  /** Messages par chemin de champ (`titre`, `dimensions.hauteur`). */
  readonly champs: Readonly<Record<string, string>>;
  /**
   * Range une erreur d'envoi : les messages par champ, quand l'API en donne, et
   * un message d'ensemble. `champsAffiches` liste les chemins que l'écran sait
   * montrer sous un champ ; les autres restent dans le message d'ensemble, pour
   * qu'aucune erreur ne disparaisse.
   */
  readonly appliquer: (erreur: unknown, champsAffiches?: readonly string[]) => void;
  readonly reinitialiser: () => void;
  /** Message du premier de ces chemins (ou de l'un de leurs sous-chemins) en erreur. */
  readonly messageChamp: (...chemins: readonly string[]) => string | undefined;
}

/**
 * État d'erreur d'un formulaire : un message d'ensemble et, quand l'API
 * renvoie `details.champs` (400 `validation.invalide`), le message de chaque
 * champ concerné à afficher sous lui (`<Field error={messageChamp('titre')}>`).
 */
export function useErreurFormulaire(): ErreurFormulaire {
  const { t } = useTranslation(['regie', 'commun']);
  const [erreur, setErreur] = useState<string | null>(null);
  const [champs, setChamps] = useState<Readonly<Record<string, string>>>({});

  const appliquer = useCallback(
    (cause: unknown, champsAffiches?: readonly string[]) => {
      const parChamp = erreursParChamp(cause, t);
      if (Object.keys(parChamp).length === 0) {
        setChamps({});
        setErreur(estApiError(cause) ? cause.message : t('commun:erreurReseau'));
        return;
      }
      setChamps(parChamp);
      const montres = (chemin: string): boolean =>
        champsAffiches === undefined ||
        champsAffiches.some((affiche) => chemin === affiche || chemin.startsWith(`${affiche}.`));
      const cachees = champsEnErreur(cause).filter(({ chemin }) => !montres(chemin));
      setErreur(
        cachees.length > 0 && estApiError(cause)
          ? cause.message
          : t('regie:validation.champsASignaler'),
      );
    },
    [t],
  );

  const reinitialiser = useCallback(() => {
    setErreur(null);
    setChamps({});
  }, []);

  const messageChamp = useCallback(
    (...chemins: readonly string[]): string | undefined => {
      for (const chemin of chemins) {
        const direct = champs[chemin];
        if (direct) return direct;
        const cle = Object.keys(champs).find((autre) => autre.startsWith(`${chemin}.`));
        if (cle) return champs[cle];
      }
      return undefined;
    },
    [champs],
  );

  return { erreur, champs, appliquer, reinitialiser, messageChamp };
}
